/**
 * Data Feeds — configure a subdomain's delivery-network data feed (static vs
 * dynamic) and review the per-platform feed it implies. The Static/Dynamic
 * choice PATCHes the routing zone-config `data_feed`; the platform table (CDN
 * provider / type / feed) is read from the domain's installed CDN platforms.
 * The per-row "Data feed" select is display-only — it mirrors the zone-level
 * choice. Subdomain-scoped via the shared scope bar.
 */
import { useMemo, useState } from 'react';
import { Rss } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafZoneConfig,
  useUpdateWafZoneConfig,
  useWafDomainPlatforms,
  wafExtractError,
  type DomainPlatform,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import styles from './WafDataFeeds.module.css';

const PAGE_SIZE = 10;

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

const DATA_FEED_ITEMS = [
  { value: 'static', label: 'Static' },
  { value: 'dynamic', label: 'Dynamic' },
];

/** Per-platform feed label implied by the zone-level data-feed mode. */
const FEED_LABEL: Record<string, string> = {
  static: 'Community static data',
  dynamic: 'Community dynamic data',
};

/** Humanize a platform's delivery type for the table. */
function cdnType(type?: string): string {
  switch ((type ?? '').toLowerCase()) {
    case 'managed':
      return 'Managed CDN';
    case 'byo':
      return 'BYO CDN';
    default:
      return type ?? '';
  }
}

/** The data-feed selector + platform table for one resolved `{ serviceId, domain }`. */
function DataFeedsBody({ serviceId, domain }: { serviceId: string; domain: string }) {
  const { show } = useToast();
  const [page, setPage] = useState(1);

  const zoneQ = useWafZoneConfig(serviceId, domain);
  const platformsQ = useWafDomainPlatforms(serviceId, domain, { page, page_size: PAGE_SIZE });

  const updateFeed = useUpdateWafZoneConfig(serviceId, {
    onSuccess: (_d, vars) => { show({ tone: 'success', title: `Data feed set to ${vars.input.data_feed}` }); trackFeatureAction('waf', 'datafeed', 'edit'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });

  const dataFeed = zoneQ.data?.data_feed ?? 'static';
  const feedLabel = FEED_LABEL[dataFeed] ?? FEED_LABEL.dynamic;

  const platforms = platformsQ.data?.items ?? [];
  const pagination = platformsQ.data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? platforms.length;

  const columns = useMemo<ColumnDef<DomainPlatform, unknown>[]>(
    () => [
      {
        id: 'provider',
        header: 'CDN provider',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ fontWeight: 500 }}>{row.original.display_name || row.original.name}</span>
        ),
      },
      {
        id: 'type',
        header: 'CDN type',
        enableSorting: false,
        cell: ({ row }) => {
          const t = cdnType(row.original.type);
          return t ? t : muted('—');
        },
      },
      {
        id: 'feed',
        header: 'Data feed',
        enableSorting: false,
        cell: () => (
          // Display-only: the feed is set at the zone level above; this mirrors it.
          <Select value="feed" disabled>
            <SelectTrigger aria-label="Data feed" style={{ maxWidth: 260 }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="feed">{feedLabel}</SelectItem>
            </SelectContent>
          </Select>
        ),
      },
    ],
    [feedLabel],
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Zone-level data feed */}
      <Card>
        <CardHeader>
          <CardTitle>Select the data feed for your website</CardTitle>
        </CardHeader>
        <CardBody>
          <p className={styles.help}>
            Static data feed uses community static data to monitor the performance of content delivery
            network to improve static content-based website.
          </p>
          <p className={styles.help}>
            Dynamic data feed uses community and private dynamic data to monitor performance of dynamic
            delivery network to improve dynamic content-based website.
          </p>
          {zoneQ.isPending ? (
            <Skeleton style={{ height: 64, width: 200, marginTop: 'var(--vn-space-3)' }} />
          ) : zoneQ.isError ? (
            <Alert
              tone="danger"
              title="Couldn't load the data feed"
              actions={<button onClick={() => zoneQ.refetch()}>Retry</button>}
            >
              {wafExtractError(zoneQ.error, "The subdomain's data-feed configuration failed to load.")}
            </Alert>
          ) : (
            <RadioGroup
              value={dataFeed}
              onValueChange={(v) => updateFeed.mutate({ domain, input: { data_feed: v } })}
              disabled={updateFeed.isPending}
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-3)' }}
            >
              {DATA_FEED_ITEMS.map((it) => (
                <label
                  key={it.value}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                >
                  <RadioGroupItem value={it.value} /> {it.label}
                </label>
              ))}
            </RadioGroup>
          )}
        </CardBody>
      </Card>

      {/* Per-platform feed table */}
      {platformsQ.isPending ? (
        <Skeleton style={{ height: 200 }} />
      ) : platformsQ.isError ? (
        <Alert
          tone="danger"
          title="Couldn't load CDN platforms"
          actions={<button onClick={() => platformsQ.refetch()}>Retry</button>}
        >
          {wafExtractError(platformsQ.error, "The subdomain's CDN platforms failed to load.")}
        </Alert>
      ) : (
        <Card>
          <CardBody>
            <DataTable
              columns={columns}
              data={platforms}
              getRowId={(r) => r.platform_id}
              emptyState={
                <EmptyState
                  icon={<Rss size={20} />}
                  title="No CDN platforms"
                  description="This subdomain has no CDN platforms enabled yet."
                />
              }
              footer={total ? `Showing ${platforms.length} of ${total}` : undefined}
            />
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--vn-space-3)' }}>
                <Pagination
                  page={page}
                  pageCount={totalPages}
                  onPageChange={setPage}
                  summary={platformsQ.isFetching ? 'Loading…' : `Page ${page} of ${totalPages}`}
                />
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

export function WafDataFeeds({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Rss size={22} />}
        title="Data Feeds"
        description="Configure the type of delivery-network data feed (static or dynamic) for a specific subdomain to suit your website's orchestration strategy."
      />

      <div className={styles.scope}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<Rss size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain. Proxy a record to configure its data feed."
        />
      ) : domain ? (
        <DataFeedsBody key={domain} serviceId={serviceId} domain={domain} />
      ) : (
        <EmptyState
          icon={<Rss size={20} />}
          title="Select a subdomain"
          description="Pick a subdomain above to configure its data feed."
        />
      )}
    </Container>
  );
}

export default WafDataFeeds;
